import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useUserData } from '../../hooks/useUserData';
import { getPlanLabelFromGroups } from '../../utils/userPlan';

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
  isMobile?: boolean;
  onLinkClick?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isCollapsed, 
  setIsCollapsed, 
  isMobile = false, 
  onLinkClick 
}) => {
  const { user: userData } = useUserData();
  const auth = useAuth();
  const planText = getPlanLabelFromGroups(userData?.groups);

  return (
    <div
      className={`${
        isMobile ? 'relative' : 'sticky top-[72px] self-start h-[calc(100vh-72px)]'
      } ${isCollapsed ? 'w-20' : 'w-64'} transition-all duration-300 ease-in-out`}
    >
      <aside className="bg-white border-r border-gray-200 shadow-sm h-full overflow-y-auto flex flex-col">
        <div className={`py-8 px-6 border-b border-gray-200 ${isCollapsed ? 'px-2' : ''}`}>
          {!isMobile && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={`mb-4 w-full flex items-center justify-center p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200 ${
                isCollapsed ? 'px-2' : ''
              }`}
              aria-label={isCollapsed ? 'Expandir navegação' : 'Recolher navegação'}
              title={isCollapsed ? 'Expandir navegação' : 'Recolher navegação'}
            >
              <svg className="h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          {!isCollapsed && auth.isAuthenticated && userData && (
            <div className="text-sm text-gray-600">
              <div className="font-semibold text-gray-800 text-base mb-1">{userData.name || 'Usuário'}</div>
              <div className="text-gray-500 text-xs">{planText}</div>
            </div>
          )}
        </div>

        <nav className="py-4 flex-1">
          <NavLink 
            to="/dashboard" 
            onClick={onLinkClick}
            className={({ isActive }) =>
            `flex items-center w-full py-4 text-left text-base font-medium cursor-pointer transition-all duration-200 ease-in-out relative no-underline ${
              isCollapsed ? 'px-6 justify-center' : 'px-6 space-x-3'
            } ${
              isActive
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`
          }>
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 pointer-events-none"></div>}
                <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500'}`}>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 3v18h18" />
                    <path d="M7 15v4" />
                    <path d="M12 11v8" />
                    <path d="M17 7v12" />
                  </svg>
                </span>
                <span className={isCollapsed ? 'hidden' : 'block'}>Dashboard</span>
              </>
            )}
          </NavLink>
          <NavLink 
            to="/empresas" 
            onClick={onLinkClick}
            className={({ isActive }) =>
            `flex items-center w-full py-4 text-left text-base font-medium cursor-pointer transition-all duration-200 ease-in-out relative no-underline ${
              isCollapsed ? 'px-6 justify-center' : 'px-6 space-x-3'
            } ${
              isActive
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`
          }>
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 pointer-events-none"></div>}
                <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500'}`}>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 21h16" />
                    <path d="M6 21V7l6-4 6 4v14" />
                    <path d="M9 9h2" />
                    <path d="M13 9h2" />
                    <path d="M9 13h2" />
                    <path d="M13 13h2" />
                    <path d="M10 21v-4h4v4" />
                  </svg>
                </span>
                <span className={isCollapsed ? 'hidden' : 'block'}>Minhas empresas</span>
              </>
            )}
          </NavLink>
          <NavLink 
            to="/configuracoes" 
            onClick={onLinkClick}
            className={({ isActive }) =>
            `flex items-center w-full py-4 text-left text-base font-medium cursor-pointer transition-all duration-200 ease-in-out relative no-underline ${
              isCollapsed ? 'px-6 justify-center' : 'px-6 space-x-3'
            } ${
              isActive
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`
          }>
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 pointer-events-none"></div>}
                <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500'}`}>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.6 1.6 0 00.3 1.7l.1.1a2 2 0 01-1.4 3.4h-.2a2 2 0 01-1.9-1.3l-.1-.2a1.6 1.6 0 00-1.5-.9 1.6 1.6 0 00-1.5.9l-.1.2a2 2 0 01-1.9 1.3h-.2a2 2 0 01-1.4-3.4l.1-.1a1.6 1.6 0 00.3-1.7 1.6 1.6 0 00-1.2-1l-.2-.1a2 2 0 010-4l.2-.1a1.6 1.6 0 001.2-1 1.6 1.6 0 00-.3-1.7l-.1-.1A2 2 0 017.6 3h.2a2 2 0 011.9 1.3l.1.2a1.6 1.6 0 001.5.9 1.6 1.6 0 001.5-.9l.1-.2A2 2 0 0114.8 3h.2a2 2 0 011.4 3.4l-.1.1a1.6 1.6 0 00-.3 1.7 1.6 1.6 0 001.2 1l.2.1a2 2 0 010 4l-.2.1a1.6 1.6 0 00-1.2 1z" />
                  </svg>
                </span>
                <span className={isCollapsed ? 'hidden' : 'block'}>Configurações</span>
              </>
            )}
          </NavLink>
          <NavLink 
            to="/assinatura" 
            onClick={onLinkClick}
            className={({ isActive }) =>
            `flex items-center w-full py-4 text-left text-base font-medium cursor-pointer transition-all duration-200 ease-in-out relative no-underline ${
              isCollapsed ? 'px-6 justify-center' : 'px-6 space-x-3'
            } ${
              isActive
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`
          }>
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 pointer-events-none"></div>}
                <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500'}`}>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="M3 10h18" />
                    <path d="M7 15h4" />
                  </svg>
                </span>
                <span className={isCollapsed ? 'hidden' : 'block'}>Assinatura</span>
              </>
            )}
          </NavLink>
          <NavLink 
            to="/perfil" 
            onClick={onLinkClick}
            className={({ isActive }) =>
            `flex items-center w-full py-4 text-left text-base font-medium cursor-pointer transition-all duration-200 ease-in-out relative no-underline ${
              isCollapsed ? 'px-6 justify-center' : 'px-6 space-x-3'
            } ${
              isActive
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`
          }>
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 pointer-events-none"></div>}
                <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500'}`}>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                </span>
                <span className={isCollapsed ? 'hidden' : 'block'}>Perfil</span>
              </>
            )}
          </NavLink>
        </nav>
      </aside>
    </div>
  );
};

export default Sidebar;
